import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, resolveDirective as _resolveDirective, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-0e75b21a"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "leak-terminal-chart-wrap" }

import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import * as echarts from 'echarts'
import { ResizeService } from '@/services/resize.service'
import { getAssignRankOption } from '@/utils/echarts-option/rank'
import { useEchartsThemeSync } from '@/composables/use-echarts-theme-sync'
import Card from '@/views/dashboard/_components/card/card.vue'
import EmptyData from '@/views/dashboard/_components/empty-data/empty-data.vue'
import { VulnerabilityFixService, LeakTerminalList } from '../../vulnerability-fix.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'leak-terminal-chart',
  emits: ["repairDrilldown"],
  setup(__props, { emit: __emit }) {

const injector = useReflectiveInjector([VulnerabilityFixService, ResizeService])
const service = injector.get(VulnerabilityFixService)
const resizeService = injector.get(ResizeService)
const { t } = useI18n()

const isLoading = ref(false)
const rawData = ref<LeakTerminalList[]>([])

const emit = __emit

const chartRef = ref<HTMLElement>()
let chartInstance: echarts.EChartsType | null = null

function getCssColorVar (name: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return v || fallback
}

const hasData = computed(() => {
  return rawData.value?.length > 0
})

/** 按漏洞总数降序（与柱图 y 轴一致，用于点击 device_id） */
const sortedTerminalRows = computed(() => {
  const list = rawData.value || []
  return [...list].sort((a, b) => (b.leak_count || 0) - (a.leak_count || 0))
})

const sortedData = computed(() => {
  const sorted = sortedTerminalRows.value
  if (sorted.length === 0) {
    return { terminal_name_list: [], high_risk_count_list: [], optional_count_list: [] }
  }

  return {
    terminal_name_list: sorted.map(item => item.user_mark || item.group_name || ''),
    high_risk_count_list: sorted.map(item => item.high_risk_count || 0),
    optional_count_list: sorted.map(
      item => (item.leak_count || 0) - (item.high_risk_count || 0),
    ),
  }
})

function createChart () {
  if (!chartRef.value) return
  chartInstance = echarts.init(chartRef.value)
}

function getChartOption () {
  const { terminal_name_list, high_risk_count_list, optional_count_list } = sortedData.value
  const themePrimary = getCssColorVar('--el-color-primary', 'var(--el-color-primary)')
  const dangerRed = getCssColorVar('--el-color-danger', '#E54545')

  const option = {
    color: [dangerRed, themePrimary],
    grid: {
      left: 100,
      right: 40,
    },
    yAxis: {
      data: terminal_name_list,
      triggerEvent: true,
      inverse: true,
      axisLabel: {
        interval: 0,
      },
    },
    series: [
      {
        name: t('safeProtectionModule.highRiskLeak'),
        data: high_risk_count_list,
        itemStyle: {
          color: dangerRed,
          borderRadius: 6
        },
      },
      {
        name: t('safeProtectionModule.optionalLeak'),
        data: optional_count_list,
        itemStyle: {
          color: themePrimary,
          borderRadius: 6
        },
      },
    ],
    legend: {
      data: [t('safeProtectionModule.highRiskLeak'), t('safeProtectionModule.optionalLeak')],
      bottom: 0,
    },
  }

  return getAssignRankOption(option, { unit: t('safeProtectionModule.countUnit') })
}

function renderChart () {
  if (!chartInstance) return
  chartInstance.setOption(getChartOption())
}

function onChartClick (params: { componentType?: string; dataIndex?: number }) {
  if (params.componentType !== 'series') return
  const idx = typeof params.dataIndex === 'number' ? params.dataIndex : -1
  if (idx < 0) return
  const row = sortedTerminalRows.value[idx]
  if (!row?.device_id || !(row.leak_count > 0)) return
  emit('repairDrilldown', row)
}

function initChart () {
  chartInstance?.dispose()
  createChart()
  renderChart()
  chartInstance?.on('click', onChartClick)
}

const resizeSubscribe = resizeService.resizeSubject.subscribe(() => {
  chartInstance?.resize()
})

function loadData () {
  isLoading.value = true
  service.getLeakTerminals({
    page: 1,
    limit: 5,
    group_id: 0,
    keyword: '',
    range: 0,
    system_type: 0,
    sort: 'leak_count',
    asc: false,
  }).then((res) => {
    rawData.value = res.list || []
  }).catch(() => {
    rawData.value = []
  }).finally(() => {
    isLoading.value = false
  })
}

watch(() => sortedData.value, () => {
  if (!hasData.value) return
  if (!chartInstance) {
    nextTick(initChart)
  } else {
    renderChart()
  }
}, { deep: true })

useEchartsThemeSync(renderChart)

onMounted(() => {
  loadData()
  if (hasData.value) {
    setTimeout(() => initChart(), 100)
  }
})

onUnmounted(() => {
  resizeSubscribe.unsubscribe()
  chartInstance?.off('click', onChartClick)
  chartInstance?.dispose()
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(Card, {
    size: "second",
    title: _ctx.$t('safeProtectionModule.leakTerminalChart'),
    class: "chart-card"
  }, {
    titleAfter: _withCtx(() => [
      _createVNode(_component_el_tag, {
        size: "small",
        type: "primary"
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _withDirectives(_createElementVNode("div", {
          ref_key: "chartRef",
          ref: chartRef,
          class: "chart-container"
        }, null, 512), [
          [_vShow, hasData.value]
        ]),
        _withDirectives(_createVNode(EmptyData, {
          height: "100%",
          "bg-color": "transparent"
        }, null, 512), [
          [_vShow, !hasData.value]
        ])
      ])
    ]),
    _: 1
  }, 8, ["title"])), [
    [_directive_loading, isLoading.value]
  ])
}
}

})